import { inject, observer } from "mobx-react";
import { useEffect, useState } from "react";
import store from "stores/store";
import { UploadImageResponse } from "./Projection";

type CrossfadeGifProps = {
    imageAgedUrl: string;
    imagePresentUrl: string;
    imageAgedBgUrl: string;
    scaleFactor?: number;
    durationMs?: number;
    blendMode?: GlobalCompositeOperation;
};

function CrossfadeGif({
    imageAgedUrl,
    imagePresentUrl,
    imageAgedBgUrl,
    scaleFactor = 1,
    durationMs = 5000,
    blendMode = "soft-light",
}: CrossfadeGifProps) {
    const [gifUrl, setGifUrl] = useState<string | null>(null);
    const [status, setStatus] = useState("🔄 Erzeuge GIF...");

    const { updateSelfieImageUrl, updateQrCodeImageUrl } = store

    useEffect(() => {
        let isCancelled = false;

        async function createGif() {
            if (typeof window === "undefined") return;
            const { default: GIF } = await import("gif.js.optimized");

            const imgAged = await loadImage(imageAgedUrl);
            const imgPresent = await loadImage(imagePresentUrl);
            const imgAgedBg = await loadImage(imageAgedBgUrl);
            const overlayImg = await loadImage("/Future_Overlay.png");

            const width = imgAged.width;
            const height = imgAged.height;

            const canvas = document.createElement("canvas");
            const off = document.createElement("canvas");
            canvas.width = off.width = width;
            canvas.height = off.height = height;
            const ctx = canvas.getContext("2d")!;
            const offCtx = off.getContext("2d")!;

            const frameCount = Math.round(durationMs / 80);
            const delay = durationMs / frameCount;

            const gif = new GIF({
                workers: 1,
                quality: 15,
                width,
                height,
                workerScript: `${window.location.origin}/gif.worker.js`,
                repeat: 0,
            });

            // const logoImg = await loadImage("/Logo.svg");
            // const logoSize = Math.round(height * 0.2);

            // SVG-Textbilder vorbereiten
            const fontSize = Math.round(height * 0.08);

            const presentTextImg = await createSvgTextImage(
                "Mein Zukunfts-Ich sagt:",
                fontSize,
                width,
                height,
                height * 0.12
            );

            const futureTextImg = await createSvgTextImage(
                "Mach es!",
                fontSize,
                width,
                height,
                height * 0.88
            );


            for (let i = 0; i <= frameCount; i++) {
                const t = i / frameCount;
                const eased = 0.5 - 0.5 * Math.cos(Math.PI * t);

                offCtx.clearRect(0, 0, width, height);

                // ---------- 1️⃣ Hintergrundüberblendung ----------
                offCtx.save();

                // Junges Gradient-Hintergrund blendet aus
                const grad = offCtx.createLinearGradient(0, 0, 0, height);
                grad.addColorStop(0, "#285D68");
                grad.addColorStop(1, "#060E0E");
                offCtx.fillStyle = grad;
                offCtx.globalAlpha = 1 - Math.pow(eased, 1.5);
                offCtx.fillRect(0, 0, width, height);
                offCtx.restore();

                // Gealtertes Hintergrundbild blendet ein
                offCtx.save();
                offCtx.globalAlpha = Math.pow(eased, 1.5);
                drawImageCover(offCtx, imgAgedBg, width, height, 1);
                offCtx.restore();

                // ---------- 2️⃣ Gesichter ----------
                offCtx.save();
                // altes Gesicht bleibt immer sichtbar
                drawImageCover(offCtx, imgAged, width, height, 1);
                offCtx.restore();

                offCtx.save();
                // junges Gesicht blendet aus
                offCtx.globalAlpha = 1 - Math.pow(eased, 1.5);
                drawImageCover(offCtx, imgPresent, width, height, scaleFactor);
                offCtx.restore();


                // Jetzt das junge Bild normal (unter der Maske)
                offCtx.save();
                offCtx.globalCompositeOperation = "destination-over";
                drawImageCover(offCtx, imgPresent, width, height, scaleFactor);
                offCtx.restore();

                // Overlay 
                offCtx.save();
                offCtx.globalAlpha = 1;
                drawImageCover(offCtx, overlayImg, width, height, 1);
                offCtx.restore();

                // Texte
                offCtx.globalAlpha = 1 - eased;
                offCtx.drawImage(presentTextImg, 0, 0, width, height);

                offCtx.globalAlpha = eased;
                offCtx.drawImage(futureTextImg, 0, 0, width, height);

                // Wasserzeichen
                // offCtx.save();
                // offCtx.globalAlpha = 0.8;
                // offCtx.drawImage(logoImg, 0, height - logoSize - 5, logoSize + 25, logoSize);
                // offCtx.restore();

                // Frame speichern
                ctx.clearRect(0, 0, width, height);
                ctx.drawImage(off, 0, 0);
                gif.addFrame(ctx, { copy: true, delay });
            }


            // Letztes Frame – FUTURE + Logo sichtbar
            offCtx.clearRect(0, 0, width, height);
            const gradEnd = offCtx.createLinearGradient(0, 0, 0, height);
            gradEnd.addColorStop(0, "#285D68");
            gradEnd.addColorStop(1, "#060E0E");
            offCtx.fillStyle = gradEnd;
            offCtx.fillRect(0, 0, width, height);
            drawImageCover(offCtx, imgAgedBg, width, height, 1);
            drawImageCover(offCtx, imgAged, width, height, 1);

            offCtx.save();
            offCtx.globalAlpha = 1;
            drawImageCover(offCtx, overlayImg, width, height, 1);
            offCtx.restore();
            offCtx.globalAlpha = 1;
            offCtx.drawImage(futureTextImg, 0, 0, width, height);
            // offCtx.globalAlpha = 0.8;
            // offCtx.drawImage(logoImg, 0, height - logoSize - 5, logoSize + 25, logoSize);



            ctx.drawImage(off, 0, 0);
            gif.addFrame(ctx, { copy: true, delay: 3000 });

            gif.on("finished", async (blob: Blob) => {
                if (isCancelled) return;

                const url = URL.createObjectURL(blob);
                setGifUrl(url);
                updateSelfieImageUrl(url ? url : "");
                setStatus("✅ Fertig");

                try {
                    const formData = new FormData();
                    formData.append("file", blob, "selfie.gif");

                    if (!process.env.NEXT_PUBLIC_FILE_UPLOAD_SERVICE_URL) {
                        throw Error("NEXT_PUBLIC_FILE_UPLOAD_SERVICE_URL not set");
                    }

                    const response = await fetch(process.env.NEXT_PUBLIC_FILE_UPLOAD_SERVICE_URL, {
                        method: "POST",
                        body: formData,
                    });

                    const result = (await response.json()) as UploadImageResponse;
                    const uploadedImageUrl = result.url;
                    if (uploadedImageUrl) {
                        updateQrCodeImageUrl(uploadedImageUrl);
                    }
                } catch (error) {
                    console.error("Error uploading selfie:", error);
                }
            });


            gif.render();
        }



        createGif();

        return () => {
            isCancelled = true;
        };
    }, [imageAgedUrl, imagePresentUrl, imageAgedBgUrl, scaleFactor, durationMs, blendMode]);

    return (
        <div style={{ textAlign: "center", padding: 0 }}>
            {gifUrl && (
                <img
                    src={gifUrl}
                    alt="Crossfade GIF"
                    style={{
                        maxWidth: "100%",
                        display: "block",
                        margin: "0 auto",
                        background: "#fff",
                    }}
                    className="w-full h-full rounded-[8px]"
                />
            )}
        </div>
    );
}

export default inject("store")(observer(CrossfadeGif));


function drawImageCover(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    width: number,
    height: number,
    scaleFactor = 1
) {
    const imgRatio = img.width / img.height;
    const canvasRatio = width / height;

    let drawW, drawH;
    if (imgRatio > canvasRatio) {
        drawH = height * scaleFactor;
        drawW = drawH * imgRatio;
    } else {
        drawW = width * scaleFactor;
        drawH = drawW / imgRatio;
    }

    const offsetX = (width - drawW) / 2;
    const offsetY = (height - drawH) / 2;
    ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

async function createSvgTextImage(
    text: string,
    fontSize: number,
    width: number,
    height: number,
    yPosition: number
): Promise<HTMLImageElement> {
    const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <style>
        text {
          font: bold ${fontSize}px Frutiger, Arial, sans-serif;
          fill: white;
          stroke: black;
          stroke-width: ${Math.max(2, fontSize * 0.06)};
          paint-order: stroke fill;
          text-anchor: middle;
          dominant-baseline: middle;
        }
      </style>
      <text x="50%" y="${yPosition}">${text}</text>
    </svg>`;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
    await new Promise((r) => (img.onload = r));
    return img;
}
